<template>
    <div class="wl-page">
        <div class="wl-header">
            <div class="wl-header-left">
                <div class="wl-icon-wrap"><i class="fas fa-map-marker-alt"></i></div>
                <h2 class="wl-title">Work Locations</h2>
                <span class="wl-count" v-if="items.length">{{ items.length }}</span>
            </div>
            <AllowPermission :permission="PERMISSION_CODES.CREATE_WORK_LOCATIONS">
                <button class="wl-add-btn" @click="$router.push('/work-locations/create')">
                    <i class="fas fa-plus"></i> New Location
                </button>
            </AllowPermission>
        </div>

        <div v-if="loading" class="wl-loading"><div class="wl-spinner"></div><span>Loading...</span></div>
        <div v-else-if="!items.length" class="wl-empty">
            <div class="wl-empty-icon"><i class="fas fa-map-marker-alt"></i></div>
            <h3>No work locations found</h3>
        </div>
        <div v-else class="wl-grid">
            <div class="wl-card" v-for="(item, idx) in items" :key="item.id" :style="{ animationDelay: `${idx * 0.03}s` }">
                <div class="wl-card-top">
                    <div class="wl-card-icon" :class="wlColor(idx)"><i class="fas fa-map-marker-alt"></i></div>
                    <div class="wl-card-info">
                        <h4 class="wl-card-name">{{ item.name }}</h4>
                        <div class="wl-card-loc" v-if="item.city_data || item.country_data">
                            <i class="fas fa-globe-americas"></i>
                            {{ [item.city_data?.name, item.country_data?.name].filter(Boolean).join(', ') }}
                        </div>
                    </div>
                </div>
                <div class="wl-card-meta" v-if="item.zipCode">
                    <span class="wl-meta-label">Zip Code</span>
                    <span class="wl-meta-val">{{ item.zipCode }}</span>
                </div>
                <div class="wl-card-actions">
                    <AllowPermission :permission="PERMISSION_CODES.EDIT_WORK_LOCATIONS">
                        <a class="wl-action-btn wl-btn-edit" :href="$router.resolve({ name: 'work-location-edit', params: { id: item.id } }).href">
                            <i class="fas fa-pen"></i> Edit
                        </a>
                    </AllowPermission>
                    <AllowPermission :permission="PERMISSION_CODES.DELETE_WORK_LOCATIONS">
                        <button class="wl-action-btn wl-btn-delete" @click="deleteItem(item.id)"><i class="fas fa-trash-alt"></i> Delete</button>
                    </AllowPermission>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import { PERMISSION_CODES } from "../../store/permission"
import AllowPermission from "../../components/layout/AllowPermission.vue"

export default {
    data() {
        return { PERMISSION_CODES, loading: true, items: [] }
    },
    components: { AllowPermission },
    async mounted() { await this.fetchData(); this.loading = false },
    methods: {
        wlColor(i) { return ["wl-clr-blue","wl-clr-purple","wl-clr-teal","wl-clr-amber","wl-clr-rose"][i % 5] },
        async fetchData() { try { const r = await http.get("/work-locations"); this.items = r.data.result || [] } catch(e) { console.log(e) } },
        async deleteItem(id) {
            if (!confirm("Delete this work location?")) return
            try { await http.delete(`/work-locations/${id}`); this.$toast.success("Deleted"); this.items = this.items.filter(c => c.id !== id) }
            catch(e) { this.$toast.error("Error deleting") }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";
.wl-page { animation: wlFade .3s ease; }
@keyframes wlFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.wl-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.wl-header-left { display:flex; align-items:center; gap:10px; }
.wl-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.wl-title { font-size:15px; font-weight:700; color:$gray800; margin:0; }
.wl-count { min-width:20px; height:20px; padding:0 6px; border-radius:$radiusFull; font-size:11px; font-weight:700; background:$primaryColor; color:white; display:inline-flex; align-items:center; justify-content:center; }
.wl-add-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border:none; border-radius:6px; background:$primaryColor; color:white; font-size:12.5px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; &:hover { background:$primaryDark; } }
.wl-loading { display:flex; align-items:center; justify-content:center; gap:10px; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; span { font-size:13px; color:$gray400; } }
.wl-spinner { width:24px; height:24px; border:2.5px solid $gray200; border-top-color:$primaryColor; border-radius:50%; animation:wlSpin .7s linear infinite; }
@keyframes wlSpin { to { transform:rotate(360deg); } }
.wl-empty { text-align:center; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; h3 { font-size:14px; font-weight:700; color:$gray600; margin:0; } }
.wl-empty-icon { width:48px; height:48px; border-radius:$radiusFull; background:$gray50; border:1px solid $gray100; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; i { font-size:20px; color:$gray300; } }
.wl-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.wl-card { background:white; border:1px solid $gray100; border-radius:10px; padding:14px; box-shadow:$shadowSm; display:flex; flex-direction:column; gap:10px; animation:wlSlide .25s ease both; transition:all $transitionFast; &:hover { border-color:$gray200; box-shadow:$shadowMd; transform:translateY(-1px); } }
@keyframes wlSlide { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.wl-card-top { display:flex; align-items:flex-start; gap:10px; }
.wl-card-icon { width:36px; height:36px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.wl-clr-blue { background:#eff6ff; color:#2563eb; }
.wl-clr-purple { background:#f5f3ff; color:#7c3aed; }
.wl-clr-teal { background:#f0fdfa; color:#0d9488; }
.wl-clr-amber { background:#fffbeb; color:#d97706; }
.wl-clr-rose { background:#fff1f2; color:#e11d48; }
.wl-card-info { flex:1; min-width:0; }
.wl-card-name { font-size:13.5px; font-weight:700; color:$gray800; margin:0 0 3px; line-height:1.3; }
.wl-card-loc { font-size:11px; color:$gray400; font-weight:500; display:inline-flex; align-items:center; gap:4px; i { font-size:10px; } }
.wl-card-meta { display:flex; align-items:center; justify-content:space-between; background:$gray50; border:1px solid $gray100; border-radius:6px; padding:6px 10px; }
.wl-meta-label { font-size:11px; color:$gray400; font-weight:500; }
.wl-meta-val { font-size:12px; color:$gray700; font-weight:600; }
.wl-card-actions { display:flex; gap:6px; margin-top:auto; padding-top:6px; border-top:1px solid $gray100; }
.wl-action-btn { display:inline-flex; align-items:center; gap:4px; padding:4px 10px; border:1px solid $gray200; border-radius:5px; font-size:11px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; background:white; text-decoration:none; i { font-size:10px; } }
.wl-btn-edit { color:$primaryColor; &:hover { background:$primaryLight; border-color:rgba(0,94,137,0.15); } }
.wl-btn-delete { color:$danger; &:hover { background:#fef2f2; border-color:rgba(220,38,38,0.15); } }
@media(max-width:992px) { .wl-grid { grid-template-columns:repeat(2,1fr); } }
@media(max-width:480px) { .wl-grid { grid-template-columns:1fr; } }
</style>
